import React, { useEffect, useId, useState } from 'react'
import '../styles/searchable-select.css'

const DEFAULT_RESULTS_LIMIT = 100

export default function SearchableSelect ({
  className = '',
  disabled = false,
  error = false,
  inputClassName = '',
  maxResults = DEFAULT_RESULTS_LIMIT,
  onChange,
  // Вызывается с введённым текстом: список, который целиком не загружен
  // (справочник большого аккаунта), догружает совпадения с сервера.
  onQueryChange,
  optionClassName = '',
  options = [],
  placeholder = 'Выберите',
  value = '',
  ...props
}) {
  const listboxId = useId()
  const normalizedValue = value == null ? '' : String(value)
  const selected = options.find(option => String(option.value) === normalizedValue)
  const [query, setQuery] = useState(selected?.label || '')
  const [open, setOpen] = useState(false)
  const [filtering, setFiltering] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)

  useEffect(() => {
    setQuery(selected?.label || '')
    setFiltering(false)
  }, [selected?.label, selected?.value])

  const normalizedQuery = filtering ? query.trim().toLocaleLowerCase('ru') : ''
  const matches = options.filter(option => {
    const searchable = [option.label, option.value, option.groupLabel].filter(Boolean).join(' ')
    return !normalizedQuery || searchable.toLocaleLowerCase('ru').includes(normalizedQuery)
  })
  const visibleOptions = matches.slice(0, maxResults)

  function choose (option) {
    if (option.disabled) return
    onChange(String(option.value))
    setQuery(option.label)
    setOpen(false)
    setFiltering(false)
    setActiveIndex(-1)
  }

  function handleChange (event) {
    const nextQuery = event.target.value
    const exact = options.find(option =>
      String(option.value) === nextQuery || option.label === nextQuery
    )
    setQuery(nextQuery)
    setOpen(true)
    setFiltering(true)
    setActiveIndex(-1)
    onQueryChange?.(nextQuery)
    if (exact && !exact.disabled) choose(exact)
  }

  function handleKeyDown (event) {
    if (event.key === 'Escape') {
      setOpen(false)
      setActiveIndex(-1)
      return
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      setOpen(true)
      setActiveIndex(current => {
        const enabledIndexes = visibleOptions
          .map((option, index) => option.disabled ? -1 : index)
          .filter(index => index >= 0)
        if (enabledIndexes.length === 0) return -1
        const position = enabledIndexes.indexOf(current)
        if (event.key === 'ArrowDown') {
          return enabledIndexes[position >= enabledIndexes.length - 1 ? 0 : position + 1]
        }
        return enabledIndexes[position <= 0 ? enabledIndexes.length - 1 : position - 1]
      })
      return
    }
    if (event.key === 'Enter' && open && activeIndex >= 0 && visibleOptions[activeIndex]) {
      event.preventDefault()
      choose(visibleOptions[activeIndex])
    }
  }

  let previousGroup = null
  return (
    <div className={`searchable-select ${className}`.trim()}>
      <input
        {...props}
        aria-activedescendant={open && activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined}
        aria-autocomplete='list'
        aria-controls={listboxId}
        aria-expanded={open}
        aria-invalid={error || undefined}
        autoComplete='off'
        className={`ui-input searchable-select__input ${inputClassName}`.trim()}
        disabled={disabled}
        placeholder={placeholder}
        role='combobox'
        value={query}
        onBlur={() => {
          setOpen(false)
          setFiltering(false)
          setActiveIndex(-1)
          setQuery(selected?.label || '')
        }}
        onChange={handleChange}
        onFocus={event => {
          setOpen(true)
          setFiltering(false)
          event.currentTarget.select()
        }}
        onKeyDown={handleKeyDown}
      />
      <span aria-hidden='true' className='searchable-select__chevron'>⌄</span>
      {open && !disabled && (
        <div className='searchable-select__options' id={listboxId} role='listbox'>
          {visibleOptions.map((option, index) => {
            const groupChanged = option.groupLabel && option.groupLabel !== previousGroup
            previousGroup = option.groupLabel || null
            return (
              <React.Fragment key={`${option.value}-${index}`}>
                {groupChanged && <span className='searchable-select__group'>{option.groupLabel}</span>}
                <button
                  aria-selected={String(option.value) === normalizedValue}
                  className={`searchable-select__option ${optionClassName}`.trim()}
                  data-active={index === activeIndex || undefined}
                  id={`${listboxId}-option-${index}`}
                  role='option'
                  type='button'
                  disabled={option.disabled}
                  onMouseDown={event => {
                    event.preventDefault()
                    choose(option)
                  }}
                  onClick={() => {
                    if (String(option.value) !== normalizedValue) choose(option)
                  }}
                >
                  {option.label}
                </button>
              </React.Fragment>
            )
          })}
          {visibleOptions.length === 0 && <span className='searchable-select__empty'>Ничего не найдено</span>}
          {matches.length > maxResults && (
            <span className='searchable-select__hint'>Показаны первые {maxResults}. Уточните поиск.</span>
          )}
        </div>
      )}
    </div>
  )
}
